/**
 * Plan checkout — the one implementation behind every plan CTA on every landing
 * page (tws-pricing, tws-compare-plans, tws-benefits-plans, wp-plans).
 *
 * Two CTA shapes, one path:
 *   one button per plan    <button data-tws-checkout data-variant-id data-selling-plan>
 *   radio group + button   <button data-tws-checkout data-tws-checkout-group="<radio name>">
 *
 * A Shopify cart write costs ~800ms server-side. That is a platform floor — an
 * empty Allbirds cart measures the same — so the only lever is making the click
 * pay for one write instead of two. Any leftover plan line is therefore zeroed
 * in the background on page load, while the shopper is still reading, which
 * leaves add.js as the only request on the critical path.
 *
 * add.js carries the competition id as both a line property and a cart
 * attribute, which is what Boost / upsells read, so no separate update.js.
 *
 * Free gifts: when the page's Campaign settings turn them on
 * (window.TWS_FREE_GIFTS_ENABLED), the variant ids in the plan's
 * data-free-gifts ride along in the same add.js, tagged _free_gift so a
 * leftover gift is cleared with its plan. A gift with an entry in
 * data-free-gift-notes also gets a visible "Shipment" property for checkout.
 *
 * The clicked button spins (see [data-tws-checkout][aria-busy] in tws.css).
 */
(function () {
  /* Sections each pull this file in, so a page with two of them (pricing +
     compare, or either one alongside wp-plans) executes it twice and would
     bind two click listeners — one click, two add.js, quantity 2. */
  if (window.twsCheckoutReady) return;
  window.twsCheckoutReady = true;

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  var jsonHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  function buttons() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-tws-checkout]'));
  }

  function radiosFor(group) {
    return Array.prototype.slice.call(document.getElementsByName(group));
  }

  /* Where a CTA reads its variant and selling plan from: itself, or the checked
     radio of the group it drives. */
  function sourceFor(button) {
    var group = button.getAttribute('data-tws-checkout-group');
    if (!group) return button;

    return (
      radiosFor(group).filter(function (radio) {
        return radio.checked;
      })[0] || null
    );
  }

  function getCompetitionId() {
    return String(
      window.TWS_COMPETITION_ID ||
        document.documentElement.getAttribute('data-competition-id') ||
        ''
    ).trim();
  }

  function freeGiftsEnabled() {
    return window.TWS_FREE_GIFTS_ENABLED === true;
  }

  /* { "<variant id>": "Ships after 3 months" } from the plan's
     custom.free_gift_notes, shown under that gift in cart and checkout. */
  function freeGiftNotes(source) {
    try {
      return JSON.parse(source.getAttribute('data-free-gift-notes') || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function freeGiftItems(source, properties) {
    if (!freeGiftsEnabled()) return [];

    var notes = freeGiftNotes(source);

    return String(source.getAttribute('data-free-gifts') || '')
      .split(',')
      .map(function (id) {
        return Number(id);
      })
      .filter(function (id) {
        return id > 0;
      })
      .map(function (id) {
        var giftProperties = { _free_gift: 'true' };
        if (properties._competition_id) {
          giftProperties._competition_id = properties._competition_id;
        }
        if (notes[id]) giftProperties.Shipment = String(notes[id]);
        return { id: id, quantity: 1, properties: giftProperties };
      });
  }

  function getRoll() {
    var picker = document.querySelector('tws-watch-roll, watch-roll-picker');
    if (!picker) return null;

    if (typeof picker.getCheckoutPayload === 'function') {
      var live = picker.getCheckoutPayload();
      if (live && (live.ids || (live.picks && live.picks.length))) return live;
    }
    if (window.WPK_ROLL) return window.WPK_ROLL;

    try {
      return JSON.parse(sessionStorage.getItem('wpk-roll') || 'null');
    } catch (error) {
      return null;
    }
  }

  function addRollProperties(properties, roll) {
    if (!roll) return;
    if (roll.ids) properties['_Watch pick IDs'] = String(roll.ids);
    if (roll.names) properties['_Watch picks'] = String(roll.names);
    if (roll.entryMode) properties['_Entry mode'] = String(roll.entryMode);
    if (typeof roll.remaining === 'number') {
      properties['_Budget remaining'] = String(Math.round(roll.remaining));
    }
    if (typeof roll.used === 'number') {
      properties['_Budget used'] = String(Math.round(roll.used));
    }
    if (roll.picks && roll.picks.length) {
      try {
        var compact = JSON.stringify(roll.picks);
        if (compact.length > 450) compact = compact.slice(0, 447) + '...';
        properties['_Watch roll JSON'] = compact;
      } catch (error) {
        /* ignore */
      }
    }
  }

  /* Every plan variant this page can add, so leftovers can be recognised
     without touching anything else the shopper put in their cart. */
  function planVariantIds() {
    var ids = {};

    buttons().forEach(function (button) {
      var group = button.getAttribute('data-tws-checkout-group');
      var sources = group ? radiosFor(group) : [button];

      sources.forEach(function (source) {
        var id = source && source.getAttribute && source.getAttribute('data-variant-id');
        if (id) ids[Number(id)] = true;
      });
    });

    return ids;
  }

  function dropLeftoverPlanLines(cart) {
    if (!cart || !cart.items || !cart.items.length) return cart;

    var ids = planVariantIds();
    var updates = {};
    var found = false;

    cart.items.forEach(function (line) {
      var isGift = line && line.properties && line.properties._free_gift;
      if (line && line.key && (ids[line.variant_id] || isGift)) {
        updates[line.key] = 0;
        found = true;
      }
    });

    if (!found) return cart;

    return fetch(routesRoot() + 'cart/update.js', {
      method: 'POST',
      headers: jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (res) {
        return res.ok ? res.json() : cart;
      })
      .catch(function () {
        return cart;
      });
  }

  /* Runs on load and on bfcache restore. A stale snapshot is what previously
     let a back-button re-click add the same membership a second time. */
  var cartReady = null;

  function primeCart() {
    cartReady = fetch(routesRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(dropLeftoverPlanLines)
      .catch(function () {
        return null;
      });

    return cartReady;
  }

  function addPlan(body) {
    return fetch(routesRoot() + 'cart/add.js', {
      method: 'POST',
      headers: jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  function showError(button, message) {
    var scope =
      button.closest('.tws-section, .plans-section, [id^="shopify-section"]') || document;
    var target = scope.querySelector('[data-tws-checkout-error], [data-plans-cta-error]');

    if (!target) {
      if (message) window.alert(message);
      return;
    }
    target.textContent = message || '';
    target.hidden = !message;
  }

  function lock(button) {
    buttons().forEach(function (other) {
      other.disabled = true;
    });
    button.setAttribute('aria-busy', 'true');
  }

  function unlock() {
    buttons().forEach(function (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    });
  }

  function money(value) {
    return typeof window.twsTrackMoney === 'function' ? window.twsTrackMoney(value) : 0;
  }

  function analyticsFor(source, variantId) {
    var planKey = source.getAttribute('data-plan') || '';
    var label = source.getAttribute('data-cta-label') || (source.textContent || '').trim();
    var price = source.getAttribute('data-cta-price') || '';
    var was = source.getAttribute('data-cta-was') || '';
    var value = money(price);
    var wasValue = money(was);
    var currency =
      (window.twsTrackMarket && window.twsTrackMarket().currency) ||
      (window.TWS_MARKET && window.TWS_MARKET.currency) ||
      '';

    return {
      tier: planKey,
      price: price,
      compare_at: was,
      compare_price: was,
      tws_membership_plan_key: planKey,
      tws_product_id: variantId,
      tws_product_name: label,
      tws_value: value,
      tws_compare_at: wasValue || '',
      tws_currency: currency,
      tws_quantity: 1,
      currency: currency,
      value: value,
      item_id: variantId,
      item_name: label,
      quantity: 1,
      discount: wasValue > value ? +(wasValue - value).toFixed(2) : 0,
    };
  }

  function track(event, payload) {
    if (typeof window.twsTrack === 'function') window.twsTrack(event, payload);
  }

  function checkout(button) {
    var source = sourceFor(button);
    if (!source) {
      showError(button, 'Select a membership plan to continue.');
      return;
    }
    if (source.getAttribute('data-unavailable') === 'true') {
      showError(button, 'That plan is currently unavailable. Choose another plan.');
      return;
    }

    var variantId = source.getAttribute('data-variant-id');
    if (!variantId) {
      showError(
        button,
        'Connect each plan to a Shopify product in the theme editor (product + selling plan for subscriptions).'
      );
      return;
    }

    var mode = button.getAttribute('data-checkout-mode') || 'checkout';
    var destination = routesRoot() + (mode === 'cart' ? 'cart' : 'checkout');
    var sellingPlan = source.getAttribute('data-selling-plan') || '';
    var competitionId = getCompetitionId();

    /* Do not add a visible "Compare at" line property — Shopify checkout
       already strikes through the product/selling-plan compare-at price when
       that value is set in admin. Watchpick was the only path that wrote this. */
    var properties = {};
    if (competitionId) properties._competition_id = competitionId;
    addRollProperties(properties, getRoll());

    var item = { id: Number(variantId), quantity: 1, properties: properties };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    /* Shopify lists add.js items last-first, so the plan goes last to show
       above its gifts in cart and checkout. */
    var payload = { items: freeGiftItems(source, properties).concat([item]) };
    if (competitionId) {
      payload.attributes = {
        competition_id: competitionId,
        _competition_id: competitionId,
      };
    }

    var analytics = analyticsFor(source, variantId);
    track('clicked_get_offer_checkout', analytics);

    showError(button, '');
    lock(button);

    /* cartReady already absorbed the leftover clear, so this is one write. */
    cartReady
      .then(function () {
        return addPlan(payload);
      })
      .then(function (response) {
        return response.json().then(function (body) {
          return { ok: response.ok, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          showError(
            button,
            (result.body && (result.body.description || result.body.message)) ||
              'Could not add that plan to your cart. Check the product and selling plan in Shopify admin.'
          );
          unlock();
          return;
        }

        track('tws_add_to_cart', analytics);
        track('tws_initiate_checkout', analytics);
        window.location.href = destination;
      })
      .catch(function () {
        showError(button, 'Network error adding that plan. Please try again.');
        unlock();
      });
  }

  window.addEventListener('pageshow', function (event) {
    unlock();
    if (event.persisted) primeCart();
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest('[data-tws-checkout]');
    if (!button || button.disabled) return;

    event.preventDefault();
    checkout(button);
  });

  primeCart();
})();
